import jsPDF from "jspdf";

/**
 * Federal Pitch Packet generator.
 * Produces a 3-document PDF packet for delivery to The White House and HUD:
 *   1. Cover letter to The President
 *   2. Substantive letter to HUD Secretary Scott Turner
 *   3. Two-page Executive Briefing
 *
 * Designed for printing on letterhead OR submission digitally.
 */
export function generateFederalPitchPacket() {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const navy = [10, 25, 47];
  const red = [139, 0, 0];
  const slate = [51, 65, 85];
  const gold = [184, 134, 11];
  const margin = 64;
  let y = margin;

  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const addPageIfNeeded = (needed = 30) => {
    if (y + needed > pageH - margin) {
      doc.addPage();
      y = margin;
    }
  };

  const para = (text, opts = {}) => {
    doc.setFont(opts.font || "times", opts.style || "normal");
    doc.setFontSize(opts.size || 11);
    doc.setTextColor(...(opts.color || slate));
    const lines = doc.splitTextToSize(text, pageW - margin * 2);
    lines.forEach((line) => {
      addPageIfNeeded(14);
      doc.text(line, margin, y);
      y += opts.lineHeight || 15;
    });
    y += opts.gap ?? 8;
  };

  const heading = (text, size = 18) => {
    addPageIfNeeded(size + 16);
    doc.setFont("times", "bold");
    doc.setFontSize(size);
    doc.setTextColor(...navy);
    doc.text(text, margin, y);
    y += size + 4;
    doc.setDrawColor(...red);
    doc.setLineWidth(1.5);
    doc.line(margin, y, margin + 60, y);
    y += 16;
  };

  const subheading = (text) => {
    addPageIfNeeded(26);
    doc.setFont("times", "bold");
    doc.setFontSize(12);
    doc.setTextColor(...navy);
    doc.text(text, margin, y);
    y += 16;
  };

  const bullet = (text) => {
    doc.setFont("times", "normal");
    doc.setFontSize(11);
    doc.setTextColor(...slate);
    const lines = doc.splitTextToSize(text, pageW - margin * 2 - 16);
    lines.forEach((line, i) => {
      addPageIfNeeded(15);
      if (i === 0) {
        doc.setTextColor(...red);
        doc.text("\u25A0", margin, y);
        doc.setTextColor(...slate);
      }
      doc.text(line, margin + 16, y);
      y += 15;
    });
    y += 3;
  };

  const letterhead = (kicker) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...gold);
    doc.text("THE BRIDGE MODEL", margin, margin - 16);
    doc.setTextColor(...slate);
    doc.text("thebridgemodel.org  \u00B7  Sterile Mate LLC", pageW - margin, margin - 16, { align: "right" });
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.8);
    doc.line(margin, margin - 6, pageW - margin, margin - 6);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...red);
    doc.text(kicker, margin, margin + 6);
    y = margin + 32;
  };

  const signature = () => {
    addPageIfNeeded(120);
    y += 24;
    para("Respectfully,", { gap: 14 });
    y += 42; // signature space
    para("George Broughton III", { font: "times", style: "bold", color: navy, gap: 2 });
    para("Proprietor & Program Designer  \u00B7  The Bridge Model", { size: 10, gap: 2 });
    para("Founder & CEO  \u00B7  Sterile Mate LLC", { size: 10, gap: 2 });
    para("Case Manager Service Connector  \u00B7  The Woodlawn Organization / WCDC, Chicago, 2001\u20132007", { size: 10, gap: 2 });
    para("george.broughton@sterilemate.com  \u00B7  thebridgemodel.org", { size: 10, color: gold });
  };

  // ============================================================
  // COVER PAGE
  // ============================================================
  doc.setFillColor(...navy);
  doc.rect(0, 0, pageW, pageH, "F");

  doc.setTextColor(212, 175, 55);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("CONFIDENTIAL  /  FEDERAL EXECUTIVE COORDINATION PACKET", margin, 140);

  doc.setTextColor(249, 247, 241);
  doc.setFont("times", "bold");
  doc.setFontSize(38);
  doc.text("The Bridge Model", margin, 230);
  doc.setFontSize(18);
  doc.setFont("times", "italic");
  doc.text("Federal Endorsement & HUD Coordination Brief", margin, 264);

  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(1);
  doc.line(margin, 300, margin + 80, 300);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(203, 213, 225);
  const coverLead = doc.splitTextToSize(
    "A municipally-deployable, faith-rooted, fidelity-audited service-connector methodology, ready to launch in Baytown, Texas and scale across underserved American communities. Aligned with HUD's stated priorities in Opportunity Zones, faith-based partnerships, and workforce-housing fusion. A high-visibility win for the administration.",
    pageW - margin * 2
  );
  let cy = 332;
  coverLead.forEach((l) => {
    doc.text(l, margin, cy);
    cy += 16;
  });

  doc.setTextColor(249, 247, 241);
  doc.setFont("times", "bold");
  doc.setFontSize(13);
  doc.text("Prepared by George Broughton III", margin, pageH - 150);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(203, 213, 225);
  doc.text("Proprietor & Program Designer, The Bridge Model", margin, pageH - 134);
  doc.text("Founder & CEO, Sterile Mate LLC  \u00B7  Houston, Texas", margin, pageH - 120);
  doc.text("Trained inside The Woodlawn Organization / WCDC, Chicago (2001\u20132007)", margin, pageH - 106);
  doc.text("Under the mentorship of the late Dr. Leon Finney Jr.", margin, pageH - 92);

  // SAM.gov badge on cover
  doc.setDrawColor(212, 175, 55);
  doc.setLineWidth(0.6);
  doc.roundedRect(margin, pageH - 80, 260, 22, 2, 2);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(212, 175, 55);
  doc.text("\u2714  SAM.gov  \u00B7  ACTIVE FEDERAL REGISTRATION", margin + 8, pageH - 66);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(203, 213, 225);
  doc.setFontSize(7);
  doc.text("STERILE MATE LLC  \u00B7  CAGE 9GH66  \u00B7  ALL AWARDS  \u00B7  GRANTS & CONTRACTS", margin + 8, pageH - 56);

  doc.setTextColor(212, 175, 55);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("PACKET CONTENTS", margin, pageH - 200);
  doc.setTextColor(249, 247, 241);
  doc.text("01  Letter to the President of the United States", margin, pageH - 186);
  doc.text("02  Letter to HUD Secretary Scott Turner", margin, pageH - 173);
  doc.text("03  Executive Briefing  \u00B7  Model, Numbers, Alignment, Ask", margin, pageH - 160);

  // ============================================================
  // LETTER 01 · TO THE PRESIDENT
  // ============================================================
  doc.addPage();
  y = margin;
  letterhead("01  \u00B7  LETTER TO THE PRESIDENT OF THE UNITED STATES");

  para("The Honorable Donald J. Trump", { font: "times", style: "bold", color: navy, gap: 2 });
  para("President of the United States", { font: "times", size: 11, color: navy, gap: 2 });
  para("The White House", { font: "times", size: 11, color: navy, gap: 2 });
  para("1600 Pennsylvania Avenue NW", { font: "times", size: 11, color: navy, gap: 2 });
  para("Washington, D.C. 20500", { font: "times", size: 11, color: navy, gap: 18 });

  para(
    "Re:  A faith-rooted, work-first service-connector model ready to deploy in underserved American communities."
  ,{ font: "times", style: "bolditalic", color: navy, gap: 18 });

  para("Mr. President,", { font: "times", style: "italic", color: navy, gap: 14 });

  para(
    "The Bridge Model is a 65-year-old American methodology, born in Chicago's Woodlawn neighborhood and refined over six decades inside The Woodlawn Organization and the Woodlawn Community Development Corporation \u2014 federally-recognized, replicated in HUD literature, and tested on some of the country's most distressed public-housing portfolios. I served on that program as a Case Manager Service Connector for six continuous years under the mentorship of the late Dr. Leon Finney Jr., one of the great Black community leaders of the modern era."
  );

  para(
    "I am writing to ask for your administration's endorsement, and HUD coordination, of the first proposed municipal deployment of The Bridge Model \u2014 in Baytown, Texas \u2014 as a national showcase of what American families can achieve when given the right trusted hand to walk beside them."
  );

  para("The model directly serves the priorities your administration has championed:", { gap: 10 });

  bullet("Dignity through work, not perpetual dependency. Bridge does not warehouse families. It moves them through Lee College trade certificates into living-wage industrial and medical careers, and onto a five-year ladder to homeownership. There is an exit, by design.");
  bullet("Faith-rooted, family-centered, results-measured. Born in a faith-based community-service tradition, deployed without religious litmus tests, audited annually by independent evaluators. We measure what matters: graduates, jobs, homes purchased, ER visits prevented.");
  bullet("Local empowerment, not federal bureaucracy. Federal dollars flow to the City of Baytown, which sub-grants to three competitively-selected local agencies. The federal government's role is endorsement and pass-through, not direct operation.");
  bullet("Replicable across America. Once Baytown is proven, La Porte, Pasadena, Texas City \u2014 and any city of any political stripe \u2014 can deploy the same model under the same fidelity standards.");

  para("");
  para(
    "Mr. President, this is a low-cost, high-visibility win for the underserved American families your administration has committed to lift. I would welcome the opportunity to present The Bridge Model directly to you and to Secretary Scott Turner at HUD."
  );

  signature();

  // ============================================================
  // LETTER 02 · TO HUD SECRETARY SCOTT TURNER
  // ============================================================
  doc.addPage();
  y = margin;
  letterhead("02  \u00B7  LETTER TO HUD SECRETARY SCOTT TURNER");

  para("The Honorable Scott Turner", { font: "times", style: "bold", color: navy, gap: 2 });
  para("Secretary, U.S. Department of Housing and Urban Development", { font: "times", size: 11, color: navy, gap: 2 });
  para("451 7th Street SW", { font: "times", size: 11, color: navy, gap: 2 });
  para("Washington, D.C. 20410", { font: "times", size: 11, color: navy, gap: 18 });

  para(
    "Re:  The Bridge Model \u2014 a municipally-deployable service-connector methodology aligned with HUD's Opportunity Zone, faith-based partnership, and workforce-housing priorities."
  ,{ font: "times", style: "bolditalic", color: navy, gap: 16 });

  para("Secretary Turner,", { font: "times", style: "italic", color: navy, gap: 14 });

  para(
    "Your work standing up the White House Opportunity and Revitalization Council during the first Trump administration, and the priority you have placed on Opportunity Zones, faith-based partnerships, and workforce-housing fusion in your current role at HUD, are the reason I am writing."
  );

  para(
    "The Bridge Model is a Tri-Portfolio Hub-and-Spoke service-connector methodology developed inside The Woodlawn Organization (TWO) and the Woodlawn Community Development Corporation (WCDC) in Chicago between 1960 and 2007 \u2014 federally-recognized, cited in HUD literature, and tested on some of America's most distressed public-housing portfolios. I served on that program as a Case Manager Service Connector and Community Outreach Leader for six continuous years under the late Dr. Leon Finney Jr."
  );

  subheading("Proposed Structure");
  bullet("SAM.gov status: Sterile Mate LLC is an actively-registered federal entity (CAGE Code 9GH66; UEI on file with SAM.gov), scope: All Awards (grants & contracts). The methodology owner can transact federally on day one; the 501(c)(3) Bridge Model Foundation, Inc. is in formation as the grant-recipient intermediary.");
  bullet("Federal Funding Stream: HUD CDBG public-service set-aside as the anchor, paired with Harris County and Texas State matches. A 501(c)(3) intermediary (The Bridge Model Foundation, Inc., in formation) receives the federal funds and sub-grants to three competitively-selected local nonprofit agencies, each managing a 500-household portfolio under a 5-year contract with annual performance review.");
  bullet("Methodology Owner: Sterile Mate LLC, my Houston-based for-profit consultancy, holds the methodology, the data platform, and the train-the-trainer curriculum. It contracts separately with the Foundation under a fixed-price professional-services agreement.");
  bullet("Operating Standards: 1:38 Service Connector caseload cap; same-day intake; minimum 12\u201324 month service continuity; independent annual evaluation published as public record; non-performing agencies replaced via re-bid.");
  bullet("Year-1 Federal Ask: $4.65 million all-in (Central Coordination Hub $710k + 3 Agencies \u00D7 $1.14M + Sterile Mate Developer Group $515k) serving 1,500 households at a fully-loaded cost of approximately $230 per household per month.");

  subheading("Five-Year Outcomes (Baytown Cohort)");
  bullet("1,700 Lee College trade enrollments \u2192 670 graduates entering living-wage industrial, medical, and logistics careers.");
  bullet("985 senior home modifications keeping older adults safely aging in place.");
  bullet("$14.15M in federal benefits recovered for households eligible but not enrolled (SNAP, LIHEAP, Medicare Savings, Extra Help, VA screening).");
  bullet("920 evictions prevented through coordinated rent stabilization and rapid-response case management.");

  subheading("Alignment with HUD Priorities Under Your Leadership");
  bullet("Opportunity Zones: Baytown is partially within a designated Opportunity Zone; the model's 5-year homeownership ladder activates OZ infrastructure for working families.");
  bullet("Faith-Based Partnerships: The methodology was forged inside a faith-rooted community-service tradition while serving every household equally without religious requirement. This is the program Charitable Choice was written to enable.");
  bullet("Workforce-Housing Fusion: Trade enrollment and stable housing are managed by the same Service Connector \u2014 eliminating the handoff failures HUD case files document every year.");
  bullet("Local Empowerment: The City of Baytown is the prime; three local agencies are the sub-recipients; HUD's role is endorsement and pass-through.");
  bullet("Accountability: 1:38 caseload cap, annual independent evaluation, public scorecards. Agencies that underperform lose their contracts.");

  para("");
  para(
    "I would welcome the opportunity to present The Bridge Model to you and your team in Washington at your earliest convenience. The enclosed Executive Briefing summarizes the model in two pages; the full proposal, budget, and implementation strategy are available at thebridgemodel.org."
  );

  para(
    "Secretary Turner, this is the kind of program HUD can put a flag on, point to twelve months from now, and replicate in twenty cities by the end of the term."
  );

  signature();

  // ============================================================
  // EXECUTIVE BRIEFING
  // ============================================================
  doc.addPage();
  y = margin;
  letterhead("03  \u00B7  EXECUTIVE BRIEFING");

  heading("The Bridge Model  \u00B7  Federal Briefing", 22);

  subheading("The Model in 60 Seconds");
  para(
    "A trained Service Connector (1:38 caseload cap) is assigned to every enrolled household and walks alongside that family for 12\u201324+ months \u2014 navigating Lee College trade enrollment, Harris Health primary care, Baytown Housing Authority placements, Workforce Solutions childcare, and senior aging-in-place coordination through a single, coordinated intake. Three competitively-selected local nonprofit agencies operate the program. The City of Baytown supervises through a Central Coordination Hub. Sterile Mate LLC, the methodology owner, provides train-the-trainer curriculum and annual fidelity audits."
  );

  subheading("Numbers That Matter");
  bullet("1,500 households served per year (500 per Spoke agency).");
  bullet("$2,756 per household per year recurring cost \u2014 approximately $230 per month \u2014 to wrap an entire family in intensive case management.");
  bullet("$4.65M Year-1 federal investment, all-in (Hub + 3 Spokes + Developer Group).");
  bullet("$22.9M cumulative 5-year all-in cost \u2014 less than the cost of warehousing 250 of these same households in skilled-nursing facilities for one year.");
  bullet("3% annual cost-of-living escalator on all salaried positions; non-salary operating costs held flat.");

  subheading("Direct Alignment with HUD Priorities");
  bullet("Opportunity Zones: the Baytown proposal overlaps designated OZ tracts. The model activates OZ infrastructure for working families.");
  bullet("Faith-Based Partnerships: Faith-rooted lineage. Deployed without religious requirement. Exactly the Charitable Choice model.");
  bullet("Workforce-Housing Fusion: One Service Connector handles trade enrollment and housing simultaneously.");
  bullet("Local Empowerment: City prime, local sub-recipients, federal endorsement only.");
  bullet("Accountability: 1:38 caseload, annual third-party evaluation, public scorecards, contract loss for non-performance.");
  bullet("Replication: La Porte (2026 Q4), Pasadena (2027 Q1), Texas City (2027 Q3), Gulf Coast LMI corridor by 2028.");

  subheading("The Federal Ask");
  bullet("HUD endorsement of The Bridge Model as a high-fidelity municipal service-connector framework.");
  bullet("CDBG technical assistance designation to streamline the Baytown proposal.");
  bullet("Opportunity Zone coordination for the model's homeownership-ladder integration.");
  bullet("A 30-minute briefing with Secretary Turner and senior staff in Washington.");

  subheading("Implementation Timeline");
  bullet("Months 1\u20132  \u00B7  City Council resolution + pooled funding (CDBG, MDD, Harris County matches).");
  bullet("Months 3\u20134  \u00B7  Competitive RFP, selection of three Spoke agencies.");
  bullet("Months 4\u20137  \u00B7  Train-the-trainer for the City's 6-FTE Central Coordination Hub.");
  bullet("Months 5\u20138  \u00B7  Service Connector certification of 54 frontline FTEs across the three Spokes.");
  bullet("Month 6  \u00B7  Soft launch: 150-household pilot (50 per Spoke).");
  bullet("Months 7\u201312  \u00B7  Full launch to 1,500 households \u00B7 first annual independent evaluation published.");

  subheading("Contact");
  para(
    "George Broughton III  \u00B7  Proprietor & Program Designer, The Bridge Model  \u00B7  Founder & CEO, Sterile Mate LLC  \u00B7  george.broughton@sterilemate.com  \u00B7  thebridgemodel.org",
    { font: "times", size: 11 }
  );
  para(
    "Federal Status:  Sterile Mate LLC  \u00B7  SAM.gov Active Registration  \u00B7  CAGE Code 9GH66  \u00B7  Scope: All Awards (grants & contracts).",
    { font: "times", style: "italic", size: 10, color: navy, gap: 4 }
  );

  // Footer line
  y = pageH - margin + 6;
  doc.setDrawColor(...gold);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageW - margin, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...gold);
  doc.text("THE BRIDGE MODEL  \u00B7  A METHODOLOGY OF STERILE MATE LLC  \u00B7  thebridgemodel.org", margin, y);

  doc.save("Bridge-Model-Federal-Pitch-Packet.pdf");
}
